import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Empty, Input, Segmented, Select, Space, Spin, Table, Tag, Tooltip, Typography } from "antd";
import { ReloadOutlined, UnorderedListOutlined, AppstoreOutlined, DownOutlined, SearchOutlined } from "@ant-design/icons";
import { useDebounce } from "use-debounce";

import { controller as rootController } from "../../controller";
import { colorForTag, STATUS_PALETTE } from "../TreatmentPlans/lib/tagColors";
import { FIELD_LABELS_TINY } from "../TreatmentPlans/lib/policyCompleteness";
import TreatmentPlanListRowInsuranceActions from "../TreatmentPlans/component/TreatmentPlanListRowInsuranceActions";
import PolicyStatusCell from "../TreatmentPlans/component/PolicyStatusCell";
import { normalizeInsuranceTab, tabForRequestType } from "../InsuranceRequests/requestTypes";
import { getFTCTasks, getFTCPatientSearch } from "./controller";
import { useViewerRole } from "../viewerRole";
import FTCDataEntryModal from "./FTCDataEntryModal";
import "./ftcTasks.css";
import DashboardLayout from "../../layout/dashboardLayout/DashboardLayout";
import { pmsCarrierLabel } from "../TreatmentPlans/lib/carrierLabel";

// Priority palette — drawn from the platform-wide STATUS_PALETTE so "Requested"
// is the SAME red here, on the board tags, and everywhere else (it used to be
// orange on this page only). Requested vs urgent lean on their prefix words for
// separation, exactly as the board tags always have.
const asMeta = (label, p) => ({ label, color: p.color, bg: p.backgroundColor, border: p.borderColor });
const PRIORITY_META = {
  urgent: asMeta("Urgent", STATUS_PALETTE.urgent),
  requested: asMeta("Requested", STATUS_PALETTE.requested),
  auto: asMeta("Auto-detected", STATUS_PALETTE.auto),
};

// Work-type LABELS. Chips used to be colored by these too, but the type is
// already the first word of the label, so color spent on type said nothing new —
// while the dimension FTC users actually triage by (did a human ask for this, or
// did the system detect it?) was invisible until hover. Color now encodes ORIGIN
// via PRIORITY_META: urgent red, requested orange, auto stays the calm blue.
// The priority filter above the table uses the same three colors, so it doubles
// as the legend.
const WORK_TYPE_META = {
  breakdown: { label: "Breakdown" },
  estimate: { label: "Estimate" },
  // The word on the chip is what tells the FTC caller to ask the carrier about
  // a submitted CLAIM, not an estimate — the whole point of the third type.
  claim_followup: { label: "Claim follow-up" },
  data_entry: { label: "Data entry" },
};

// Chips for work a HUMAN asked for also carry the word, not just the color —
// identity never rests on color alone (the colorblind FTC user reads the same
// chip). Auto-detected is the unprefixed majority: loud = someone is waiting.
const URGENCY_PREFIX = { urgent: "Urgent", requested: "Requested" };

const SOURCE_LABEL = {
  request: "Requested for this policy",
  tag: "Requested (tag)",
  appointment: "Upcoming appointment, breakdown stale",
  appointment_no_policy: "Upcoming appointment, no policy on file — add one or fetch from the PMS",
  appointment_incomplete_policy: "Upcoming appointment, policy is missing details a verification needs",
  predetermination: "Predetermination submitted, estimate not fetched",
  ai_phone_call: "AI phone call",
};

const TASK_TYPE_OPTIONS = [
  { label: "All work", value: "" },
  { label: "Breakdowns", value: "breakdown" },
  { label: "Estimates", value: "estimate" },
  { label: "Claims", value: "claim_followup" },
  { label: "Data entry", value: "data_entry" },
];

// Priority filter — labels match the PriorityChip vocab ("Auto-detected" is
// surfaced to the user as "Automatic"); values are the backend priority keys.
const PRIORITY_OPTIONS = [
  { label: "Urgent", value: "urgent" },
  { label: "Requested", value: "requested" },
  { label: "Automatic", value: "auto" },
];

// Priority bands shown left-to-right in the carrier-bundle summary + their
// short label for the mini-pill tooltip.
const PRIORITY_ORDER = ["urgent", "requested", "auto"];
const PRI_SUMMARY_LABEL = { urgent: "urgent", requested: "requested", auto: "automatic" };

// Carrier badge swatches — cool/neutral hues from AntD's 1/3/7 tokens,
// deliberately avoiding the red/orange families reserved for priority. A carrier
// gets a stable color from a hash of its code so the same carrier always reads
// the same across sessions.
const CARRIER_SWATCHES = [
  { bg: "#e6f4ff", border: "#91caff", color: "#0958d9" }, // blue
  { bg: "#f0f5ff", border: "#adc6ff", color: "#1d39c4" }, // geekblue
  { bg: "#e6fffb", border: "#87e8de", color: "#08979c" }, // cyan
  { bg: "#f9f0ff", border: "#d3adf7", color: "#531dab" }, // purple
  { bg: "#fff0f6", border: "#ffadd2", color: "#c41d7f" }, // magenta
  { bg: "#f6ffed", border: "#b7eb8f", color: "#389e0d" }, // green
  { bg: "#fffbe6", border: "#ffe58f", color: "#d48806" }, // gold
  { bg: "#e6fffb", border: "#5cdbd3", color: "#006d75" }, // deep cyan
];
const NEUTRAL_SWATCH = { bg: "#f5f5f5", border: "#d9d9d9", color: "#8c8c8c" };

function carrierSwatch(code) {
  const s = String(code || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return CARRIER_SWATCHES[h % CARRIER_SWATCHES.length];
}

// Badge glyph: short all-caps acronyms (CDCP, GSC) read best in full; anything
// else collapses to a 2-letter monogram.
function carrierInitials(name, code) {
  const src = String(name || code || "").trim();
  if (!src) return "?";
  if (/^[A-Z0-9]{2,4}$/.test(src)) return src;
  const words = src.split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function priorityCounts(tasks) {
  const c = { urgent: 0, requested: 0, auto: 0 };
  (tasks || []).forEach((t) => {
    if (c[t.priority] != null) c[t.priority] += 1;
  });
  return c;
}

// List vs. carrier-grouped view. Icons + tooltips make the toggle discoverable.
const VIEW_OPTIONS = [
  {
    value: "none",
    label: (
      <Tooltip title="List view">
        <UnorderedListOutlined />
      </Tooltip>
    ),
  },
  {
    value: "carrier",
    label: (
      <Tooltip title="Group by insurance carrier">
        <AppstoreOutlined />
      </Tooltip>
    ),
  },
];

function PriorityChip({ priority }) {
  // Search results include patients with NO outstanding work, which come back
  // with a null priority — those must read as "nothing here", not fall through
  // to the "Auto-detected" chip.
  const meta = PRIORITY_META[priority];
  if (!meta) return <span className="ftc-empty-dash">—</span>;
  return (
    <span className="ftc-pri" style={{ color: meta.color, backgroundColor: meta.bg, border: "1px solid " + meta.border }}>
      {meta.label}
    </span>
  );
}

// Format an ISO appointment date ("YYYY-MM-DD") as "Jul 15 · in 3 days". Parses
// the parts as a LOCAL date so a UTC-parsed string can't shift the day, and adds
// a relative hint that reinforces the soonest-appointment-first sort.
// "follow-up due today" / "payment check overdue since Sep 28": the day an FTC
// said to check again (and what for — a claim whose payment is being processed
// is checked for the payment), parsed as a local day like formatApptDate below.
function formatFollowUp(iso, outcome) {
  const what = outcome === "payment_processing" ? "payment check" : "follow-up";
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return what + " due";
  const dt = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const label = dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return dt < today ? `${what} overdue since ${label}` : `${what} due today`;
}

function formatApptDate(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return String(iso);
  const dt = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((dt - today) / 86400000);
  const label = dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const rel = days <= 0 ? "today" : days === 1 ? "tomorrow" : `in ${days} days`;
  return `${label} · ${rel}`;
}

// Disambiguate multiple policies on the SAME carrier (e.g. primary + secondary
// CDCP) so two requests don't render as identical chips: append the tier and a
// short member-id tail. Full member id goes in the tooltip.
function policySuffix(item) {
  const bits = [];
  const tier = (item.insurance_type || "").trim();
  if (tier && tier.toLowerCase() !== "other") {
    bits.push(tier.charAt(0).toUpperCase() + tier.slice(1));
  }
  if (item.member_id) bits.push("••" + String(item.member_id).slice(-4));
  return bits.length ? " · " + bits.join(" · ") : "";
}

// "member ID and member DOB". Labels come from the shared completeness module —
// the detector, this chip and the completion form must name the same field the
// same way, or the row and the form it opens read as different work.
function describeMissingFields(fields) {
  const names = (fields || []).map((f) => FIELD_LABELS_TINY[f] || f);
  if (!names.length) return "";
  if (names.length === 1) return names[0];
  return names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
}

const WORK_TYPE_ACTION = {
  estimate: "estimate", claim_followup: "claim follow-up", data_entry: "policy details", breakdown: "breakdown",
};

// "kooshag77", not "kooshag77@gmail.com". Most prod accounts have no first/last
// name, so the attribution fallback lands on the email — the local part carries
// the identity; the domain is noise at chip size. Full detail stays on hover.
function shortActorName(actor) {
  const raw = (actor && actor.name) || "";
  const at = raw.indexOf("@");
  return at > 0 ? raw.slice(0, at) : raw;
}

function handledDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * The ✓ tail on a work chip: this policy has been handled before.
 *
 * Attribution used to render as a SEPARATE pill stacked under the chip, which
 * read as just another chip — nothing said the two were about the same task.
 * As a tail segment it is unambiguously part of the chip it annotates, and it
 * says nothing until hovered, where it answers "who touched this last, in what
 * capacity, how, when" (the compact pill used to show a bare email and nothing
 * else — the explanation lived in a tooltip nobody found).
 *
 * Renders only when the server sent attribution for this item, which it does
 * only for super admins and covering FTC personnel — absence of the field is
 * how everyone else keeps today's exact chip.
 */
function HandledTail({ handled, onSolid }) {
  if (!handled) return null;
  const actor = handled.actor;
  const who = actor
    ? `${shortActorName(actor)} (${actor.role_label})`
    : handled.via_label || "Not recorded";
  const when = handledDate(handled.at);
  const tip = (
    <>
      <div>Last handled by {who}</div>
      <div style={{ opacity: 0.75 }}>
        {[handled.via_label, when].filter(Boolean).join(" · ")}
      </div>
    </>
  );
  return (
    <Tooltip title={tip}>
      <span
        className={"ftc-work-handled" + (onSolid ? " ftc-work-handled-solid" : "")}
        aria-label={"Previously handled by " + who}
      >
        ✓
      </span>
    </Tooltip>
  );
}

/**
 * Whose desk the request sits on — "you" when it is the viewer's. Routing is
 * advisory (anyone covering the office can work the row); the tail just says
 * who it was put on, so two FTCs no longer both take a row for theirs.
 */
export function OwnerTail({ owner, onSolid }) {
  if (!owner) return null;
  const who = owner.is_me ? "you" : shortActorName(owner);
  const tip = `Assigned to ${owner.is_me ? "you" : owner.name} — anyone covering the office can work it`;
  return (
    <Tooltip title={tip}>
      <span
        className={"ftc-work-owner" + (onSolid ? " ftc-work-owner-solid" : "")}
        aria-label={"Assigned to " + (owner.is_me ? "you" : owner.name)}
      >
        {who}
      </span>
    </Tooltip>
  );
}

// A single "Work needed" chip. When `onOpen` is supplied the chip is clickable:
// it opens the right surface for the item's type with its policy pre-selected.
// `hideCarrier` drops the carrier from the label in the carrier-grouped view
// (where the bundle header already names it).
function WorkItemChip({ item, onOpen, hideCarrier }) {
  // No fallback to the Breakdown chip: an unrecognised type used to render as
  // one, which mislabels rather than degrades. Unknown types get a neutral chip
  // carrying their own name.
  const meta = WORK_TYPE_META[item.type] || { label: item.type };
  // Color by ORIGIN, not type. Unknown urgency reads as auto — calm, unprefixed.
  const pal = PRIORITY_META[item.urgency] || PRIORITY_META.auto;
  const prefix = URGENCY_PREFIX[item.urgency];
  const isDataEntry = item.type === "data_entry";
  const carrier = item.carrier_name || item.carrier_code;
  // What the office's PMS calls this carrier, when it names something else: the
  // FTC phones the insurer on the card ("GMS Insurance Inc"), not the network we
  // may have filed it under ("Telus").
  const pmsLabel = pmsCarrierLabel(carrier, item.pms_carrier_name);
  const pmsPart = pmsLabel ? " (PMS: " + pmsLabel + ")" : "";
  const typeAndCarrier = !hideCarrier && carrier
    ? meta.label + " · " + carrier + pmsPart
    : meta.label + (pmsLabel ? " · PMS: " + pmsLabel : "");
  const base = prefix ? prefix + " · " + typeAndCarrier : typeAndCarrier;
  // Data entry says WHAT to collect right on the chip, so the row is actionable
  // without opening anything. It replaces the tier/member-id suffix, which is
  // noise when the point of the row is that those values are missing.
  const missing = isDataEntry ? describeMissingFields(item.missing_fields) : "";
  // A scheduled follow-up that is due says so on the chip — it is WHY the
  // request is back in the queue today.
  const followUp = item.follow_up_on ? " · " + formatFollowUp(item.follow_up_on, item.follow_up_outcome) : "";
  const label = isDataEntry
    ? base + (missing ? " · needs " + missing : "")
    : base + policySuffix(item) + followUp;
  const clickable = typeof onOpen === "function";
  const action = WORK_TYPE_ACTION[item.type] || meta.label;
  const tip = clickable
    ? "Open " + action + (carrier ? " for " + carrier : "") + " — " + (SOURCE_LABEL[item.source] || item.source)
    : (SOURCE_LABEL[item.source] || item.source) + (item.member_id ? " · Member " + item.member_id : "");
  return (
    <Tooltip title={tip}>
      <span
        className={"ftc-work" + (clickable ? " ftc-work-clickable" : "")}
        style={{ color: pal.color, backgroundColor: pal.bg, border: "1px solid " + pal.border }}
        onClick={clickable ? (e) => { e.stopPropagation(); onOpen(item); } : undefined}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onOpen(item);
                }
              }
            : undefined
        }
      >
        {label}
        {/* Urgent chips are solid-filled with white text; the tail's green would
            vanish on that red, so it goes white there too. */}
        <HandledTail handled={item.handled_by} onSolid={pal.color === "#ffffff"} />
        <OwnerTail owner={item.assigned_to} onSolid={pal.color === "#ffffff"} />
        {clickable ? (
          <span className="ftc-work-arrow" aria-hidden="true">
            ↗
          </span>
        ) : null}
      </span>
    </Tooltip>
  );
}

/**
 * Filters carried in by a deep link from the FTC dashboard.
 *
 * Hash routing, so the query lives after the route rather than in
 * window.location.search — same reader shape as DeepLink/InsuranceLinkPage.
 */
function deepLinkParams() {
  const hash = window.location.hash || "";
  const q = hash.indexOf("?") === -1 ? "" : hash.slice(hash.indexOf("?") + 1);
  const out = {};
  q.split("&").forEach(function (pair) {
    if (!pair) return;
    const i = pair.indexOf("=");
    const k = i === -1 ? pair : pair.slice(0, i);
    out[decodeURIComponent(k)] = i === -1 ? "" : decodeURIComponent(pair.slice(i + 1));
  });
  const office = parseInt(out.office, 10);
  const assigneeId = parseInt(out.assignee, 10);
  return {
    office: Number.isFinite(office) ? office : undefined,
    // The label rides along purely for display: resolving an id to a name would
    // need a super-admin-only endpoint, and FTC users open this page too.
    assignee: Number.isFinite(assigneeId) ? { id: assigneeId, label: out.who || "" } : null,
  };
}

export default function FTCTasksPage() {
  const [tasks, setTasks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [taskType, setTaskType] = useState("");
  const [carriers, setCarriers] = useState([]);
  const [carrierOptions, setCarrierOptions] = useState([]);
  // Deep link from the FTC dashboard's Outstanding chips: #/ftc-tasks?office=&
  // assignee=&who=. Read once, on mount — after that these are ordinary filters
  // the user can clear, and re-reading would fight them.
  const [clinicId, setClinicId] = useState(() => deepLinkParams().office);
  const [assignee, setAssignee] = useState(() => deepLinkParams().assignee);
  const [priority, setPriority] = useState(undefined);
  const [groupBy, setGroupBy] = useState("none");
  // Who is looking decides the queue's default scope: FTC personnel open on
  // their own desk ("Mine" = routed to them, plus unassigned in their offices),
  // super admins on everyone's. Set once the role answers; load waits for it.
  // Routing is advisory — "Everyone" is a toggle away and anyone covering the
  // office can work any row — but two FTCs no longer both open on the same
  // backlog thinking it is theirs (Parkwoods, Oct 2026).
  const viewer = useViewerRole();
  const [scope, setScope] = useState(null);
  useEffect(() => {
    if (viewer.ready && scope === null) setScope(viewer.isSuperAdmin ? "all" : "mine");
  }, [viewer, scope]);
  // Patient search. The task list only contains patients WITH outstanding work,
  // so this searches every patient across the user's offices instead — same
  // matching as the Patients board. Debounced to match it too.
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebounce(searchQuery, 1000);
  const [searchScope, setSearchScope] = useState("all"); // "all" | "tasks"
  const [searchCounts, setSearchCounts] = useState({ all: 0, tasks: 0 });
  const [loading, setLoading] = useState(false);
  // True while the slower, Sikka-detected rows are still loading AFTER the fast
  // DB-only first paint (drives the subtle "loading additional…" indicator).
  const [loadingMore, setLoadingMore] = useState(false);
  const [status, setStatus] = useState(200);
  // Monotonic id per load() so a stale (slow) response can't overwrite a newer
  // one — covers both the fast-vs-full race and filter-change-vs-in-flight (there
  // is no request cancellation).
  const reqIdRef = useRef(0);
  // Mirror the guided tour's demo flag so the queue includes the seeded demo
  // task while the FTC tour is running (and only then).
  const [demoActive, setDemoActive] = useState(() => {
    try { return localStorage.getItem("tourDemoActive") === "1"; } catch (e) { return false; }
  });
  // Patient whose insurance modal is open, launched from a clicked work chip.
  const [insuranceTarget, setInsuranceTarget] = useState(null);
  // Policy whose data-entry modal is open: { patientId, policyId }.
  const [dataEntryTarget, setDataEntryTarget] = useState(null);
  // Carrier bundles the user has folded away (grouped view). Keyed by carrier
  // code ("other" for the carrier-less bundle).
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

  const toggleGroup = useCallback(function (key) {
    setCollapsedGroups(function (prev) {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Clinic options come from the offices cached at login (mirrors the header
  // office selector). Single-select; cleared => all of the user's offices.
  const clinicOptions = useMemo(function () {
    try {
      const raw = JSON.parse(localStorage.getItem("office_ids") || "[]");
      return (Array.isArray(raw) ? raw : []).map((o) => ({ value: o.id, label: o.office_name }));
    } catch (e) {
      return [];
    }
  }, []);

  const load = useCallback(
    async function loadInner() {
      if (scope === null) return;            // the viewer's role is still being asked
      const myId = ++reqIdRef.current;
      const isCurrent = () => reqIdRef.current === myId;
      setLoading(true);
      setLoadingMore(true);
      const searchTerm = (debouncedSearch || "").trim();
      const common = {
        scope: scope || undefined,
        taskType: taskType || undefined,
        carriers,
        officeId: clinicId || undefined,
        assigneeId: assignee ? assignee.id : undefined,
        priority: priority || undefined,
        includeTourDemo: demoActive || undefined,
      };
      // Searching swaps the data source (all patients, not just ones with work)
      // and forces the flat list — a carrier grouping of arbitrary patients is
      // meaningless. Everything below (two-pass fetch, stale-response guard,
      // painting) is shared because the two endpoints return the same envelope.
      const searching = !!searchTerm;
      const grouped = !searching && groupBy === "carrier";
      const fetchOne = (skipDetect) => {
        if (searching) {
          return getFTCPatientSearch({
            search: searchTerm,
            scope: searchScope,
            page,
            pageSize,
            officeId: clinicId || undefined,
            skipDetect,
          });
        }
        return grouped
          ? getFTCTasks({ ...common, group: "carrier", skipDetect })
          : getFTCTasks({ ...common, page, pageSize, skipDetect });
      };

      const parse = (res) => {
        const ok = res && res.status >= 200 && res.status < 300 && res.json;
        if (!ok) return { ok: false, status: res ? res.status : 0 };
        const g = grouped && Array.isArray(res.json.groups) ? res.json.groups : [];
        const rows = !grouped && Array.isArray(res.json.results) ? res.json.results : [];
        return {
          ok: true,
          status: res.status,
          groups: g,
          rows,
          count: typeof res.json.count === "number" ? res.json.count : 0,
          counts: {
            all: typeof res.json.count_all === "number" ? res.json.count_all : 0,
            tasks: typeof res.json.count_with_tasks === "number" ? res.json.count_with_tasks : 0,
          },
          empty: grouped ? g.length === 0 : rows.length === 0,
        };
      };
      const paint = (p) => {
        setStatus(p.status);
        setGroups(p.groups || []);
        setTasks(p.rows || []);
        setCount(p.count || 0);
        if (searching) setSearchCounts(p.counts);
      };

      try {
        // Fire both concurrently: the fast (DB-only) response paints ~instantly;
        // the full (Sikka-detected) response replaces it when it arrives.
        const fastReq = fetchOne(true);
        const fullReq = fetchOne(false);

        const fp = parse(await fastReq);
        if (isCurrent()) {
          if (!fp.ok) {
            setStatus(fp.status); // e.g. 403 → access gate; keep the spinner for the full pass
          } else if (!fp.empty) {
            paint(fp);
            setLoading(false); // fast rows shown; loadingMore keeps the subtle indicator
          }
          // fast + empty (e.g. a deep page has no request rows): leave the spinner
          // until the full response arrives, to avoid an empty flash.
        }

        const up = parse(await fullReq);
        if (isCurrent()) {
          if (!up.ok) {
            setStatus(up.status);
            setTasks([]);
            setGroups([]);
            setCount(0);
          } else {
            paint(up); // source of truth — replaces the fast rows (restores auto rows + dates)
          }
        }
      } catch (e) {
        console.error(e);
        if (isCurrent()) {
          setTasks([]);
          setGroups([]);
          setCount(0);
          setStatus(0);
        }
      } finally {
        if (isCurrent()) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [page, pageSize, taskType, carriers, clinicId, assignee, priority, groupBy, demoActive, debouncedSearch, searchScope, scope],
  );

  useEffect(
    function () {
      load();
    },
    [load],
  );

  // A new/cleared search starts over: page 1, and back to the All Patients tab
  // (the Tasks tab's counts belong to the previous query).
  useEffect(
    function () {
      setPage(1);
      setSearchScope("all");
      setSearchCounts({ all: 0, tasks: 0 });
    },
    [debouncedSearch],
  );

  // Keep the demo flag in sync with the guided tour (it toggles tourDemoActive +
  // fires this event), so the seeded demo task appears/disappears with the tour.
  useEffect(function () {
    const sync = function () {
      try { setDemoActive(localStorage.getItem("tourDemoActive") === "1"); } catch (e) {}
    };
    window.addEventListener("guided-onboarding-demo", sync);
    return function () { window.removeEventListener("guided-onboarding-demo", sync); };
  }, []);

  // Carrier filter options (best-effort; the filter is optional).
  useEffect(function () {
    let cancelled = false;
    (async function () {
      try {
        const res = await rootController.listInsuranceProviders();
        const providers = (res && res.data) || [];
        if (!cancelled) {
          setCarrierOptions(providers.map((p) => ({ value: p.code, label: p.name || p.code })));
        }
      } catch (e) {
        // Non-fatal — carrier dropdown just stays empty.
      }
    })();
    return function () {
      cancelled = true;
    };
  }, []);

  const refetch = useCallback(
    function () {
      load();
    },
    [load],
  );

  // Open a patient's insurance modal on `tab` with `policyId` pre-selected in
  // the "Select Policy" dropdown. The row's REAL office (the patient's) is used
  // rather than the header selectedOffice, because this view is cross-office and
  // the modal's credential reads must follow the patient.
  const openInsuranceForPolicy = useCallback(function (policyId, tab, row, item) {
    setInsuranceTarget({
      patient: Object.assign({}, row.patient, { tags: row.tags }),
      officeId: row.office && row.office.id,
      tab: normalizeInsuranceTab(tab),
      policyId,
      // The agent's suggestion for Details Needed when its fetch failed on
      // policy data: the Breakdown tab's dialog starts from it.
      suggestion: item && (item.agent_suggested_note || (item.agent_suggested_fields || []).length)
        ? { note: item.agent_suggested_note || "", fields: item.agent_suggested_fields || [] }
        : null,
    });
  }, []);

  // Clicking a work chip opens the surface that clears THAT work.
  //
  // Breakdown and estimate go to the insurance modal on the matching tab, with
  // the chip's policy pre-selected. Data entry splits:
  //   * a policy with gaps -> the completion modal, which edits just the
  //     critical fields and PATCHes only what changed;
  //   * no policy at all (policy_id null) -> the insurance modal, unchanged
  //     from how these rows behaved when they were typed as breakdowns. Opening
  //     it runs useEnsurePolicies, which fetches from the PMS.
  const openInsuranceForItem = useCallback(
    function (item, row) {
      if (item.type === "data_entry" && item.policy_id) {
        setDataEntryTarget({ patientId: row.patient.id, policyId: item.policy_id });
        return;
      }
      // A claim follow-up lands on the Claims tab, where its submissions are shown.
      openInsuranceForPolicy(item.policy_id, tabForRequestType(item.type), row, item);
    },
    [openInsuranceForPolicy],
  );

  const buildColumns = useCallback(
    function (grouped, showStatus) {
      return [
        {
          title: "Priority",
          dataIndex: "priority",
          width: 130,
          render: (value) => <PriorityChip priority={value} />,
        },
        {
          title: "Patient",
          width: 220,
          render: (_, row) => (
            <div>
              <div
                className="ftc-patient-name"
                data-onboarding-id={row.patient && row.patient.is_tour_demo ? "ftc-col-patient-name" : undefined}
              >
                {row.patient.first_name} {row.patient.last_name}
              </div>
              {(() => {
                // Notes are per-policy now: surface the flagged policies' notes
                // from the row's work items; fall back to the patient note for
                // policy-less/legacy rows.
                const items = row.work_items || [];
                const policyNotes = Array.from(
                  new Set(items.map((it) => it.attention_note).filter(Boolean)),
                );
                // The agent's own finding and the ask it suggests — this
                // payload is FTC-only, so they can be said plainly here.
                const agentNotes = [];
                items.forEach((it) => {
                  if (it.agent_note && agentNotes.indexOf("Agent: " + it.agent_note) === -1) {
                    agentNotes.push("Agent: " + it.agent_note);
                  }
                  if (it.agent_suggested_note && agentNotes.indexOf("Suggested ask: " + it.agent_suggested_note) === -1) {
                    agentNotes.push("Suggested ask: " + it.agent_suggested_note);
                  }
                  // The office's answer to our team's checklist, once it came.
                  const reply = it.info_ask;
                  if (reply && reply.answered_at) {
                    const given = (reply.answers || []).map((a) => a.label + ": " + (a.value && typeof a.value === "object"
                      ? [a.value.address, a.value.city, a.value.state, a.value.zip_code].filter(Boolean).join(", ")
                      : a.value));
                    const parts = given.concat(reply.skipped.length ? ["Not provided: " + reply.skipped.join(", ")] : [])
                      .concat(reply.answer_note ? ['"' + reply.answer_note + '"'] : []);
                    const line = "Office replied: " + (parts.join(" · ") || "nothing provided");
                    if (agentNotes.indexOf(line) === -1) agentNotes.push(line);
                  }
                });
                const shown = policyNotes.length
                  ? policyNotes
                  : (row.patient.manual_attention_note ? [row.patient.manual_attention_note] : []);
                const noteText = shown.concat(agentNotes).join("\n");
                return noteText ? (
                  <Tooltip title={noteText}>
                    <span className="ftc-note-flag">⚑ attention note</span>
                  </Tooltip>
                ) : null;
              })()}
            </div>
          ),
        },
        {
          title: "Office",
          width: 160,
          render: (_, row) => (row.office && row.office.name) || <span className="ftc-empty-dash">—</span>,
        },
        {
          title: "Appointment",
          width: 150,
          render: (_, row) =>
            row.appointment_date ? (
              <span className="ftc-appt-date" title={row.appointment_date}>
                {formatApptDate(row.appointment_date)}
              </span>
            ) : (
              <span className="ftc-empty-dash">—</span>
            ),
        },
        {
          title: "Work needed",
          render: (_, row) => (
            <span data-onboarding-id={row.patient && row.patient.is_tour_demo ? "ftc-col-work-items" : undefined}>
              <Space size={[6, 6]} wrap>
                {(row.work_items || []).map((item, idx) => (
                  <WorkItemChip
                    key={idx}
                    item={item}
                    hideCarrier={grouped}
                    onOpen={(it) => openInsuranceForItem(it, row)}
                  />
                ))}
              </Space>
            </span>
          ),
        },
        {
          // Per-policy insurance state, shared with the Patients
          // board. Only rendered while searching: task rows already say what's
          // outstanding, whereas a searched patient may have no work at all and
          // this is the only thing that tells you where their policies stand.
          title: "Status",
          key: "policy_status",
          width: 300,
          render: (_, row) => (
            <PolicyStatusCell
              policyStatuses={row.policy_statuses}
              onOpenPolicy={(policyId, tab) => openInsuranceForPolicy(policyId, tab, row)}
              className="ftc-status-cell"
            />
          ),
        },
        {
          title: "Tags",
          width: 240,
          render: (_, row) => (
            <span data-onboarding-id={row.patient && row.patient.is_tour_demo ? "ftc-col-tags" : undefined}>
              {row.tags && row.tags.length ? (
                <Space size={[4, 4]} wrap>
                  {row.tags.map((name) => (
                    <Tag key={name} color={colorForTag(name)} style={{ marginInlineEnd: 0 }}>
                      {name}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <span className="ftc-empty-dash">—</span>
              )}
            </span>
          ),
        },
        {
          title: "",
          key: "actions",
          width: 230,
          align: "right",
          render: (_, row) => (
            <TreatmentPlanListRowInsuranceActions
              patient={Object.assign({}, row.patient, { tags: row.tags })}
              officeId={row.office && row.office.id}
              isTourDemo={!!(row.patient && row.patient.is_tour_demo)}
              onPatientInsuranceUpdated={refetch}
              onInsuranceModalClosed={refetch}
            />
          ),
        },
      ].filter((col) => showStatus || col.key !== "policy_status");
    },
    [refetch, openInsuranceForItem, openInsuranceForPolicy],
  );

  // A search is "active" once the debounced term lands — that's what swaps the
  // data source, so the UI must key off the same value to stay in step.
  const searching = !!(debouncedSearch || "").trim();
  const flatColumns = useMemo(() => buildColumns(false, searching), [buildColumns, searching]);
  const groupedColumns = useMemo(() => buildColumns(true, false), [buildColumns]);

  if (status === 403) {
    return (
      <DashboardLayout>
        <div className="ftc-tasks-page">
          <Empty description="You don't have access to FTC Tasks." />
        </div>
      </DashboardLayout>
    );
  }

  // Searching forces the flat list — grouping arbitrary patients by carrier
  // says nothing useful.
  const grouped = !searching && groupBy === "carrier";

  return (
    <DashboardLayout>
    <div className="ftc-tasks-page">
      <div className="ftc-tasks-header">
        <Typography.Title level={4} style={{ marginBottom: 0 }}>
          FTC Tasks
        </Typography.Title>
        <Space>
          <Typography.Text type="secondary">
            {searching
              ? `${count} patient${count === 1 ? "" : "s"}`
              : `${count} task${count === 1 ? "" : "s"}`}
          </Typography.Text>
          {loadingMore && !loading && (
            <Typography.Text type="secondary" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Spin size="small" />
              loading additional…
            </Typography.Text>
          )}
          <Button icon={<ReloadOutlined />} onClick={refetch} loading={loading}>
            Refresh
          </Button>
        </Space>
      </div>
      <p className="ftc-tasks-subtitle">
        Breakdown, estimate, claim follow-up &amp; data-entry work across all your offices — requested tags first, then auto-detected from upcoming
        appointments and predetermination submissions. Data entry lists policies missing the details a verification needs, which is
        what makes a carrier answer &ldquo;we couldn&rsquo;t find the claimant&rdquo;. Carriers we automate are handled in the background.
        A breakdown you flag Details Needed by Smilepass — or an estimate or claim you log as Resubmit to Carrier, Correspondence
        sent to patient/member or Details Needed by Smilepass — waits with the office and leaves this queue until they resubmit it; one you log as Payment being processed
        or Queued for follow-up comes back here on the day you pick. A breakdown you can&rsquo;t get from the carrier is
        closed with Unable to obtain breakdown and a note saying why.
      </p>

      <div className="ftc-tasks-filters">
        {/* The task-centric filters are meaningless against a patient search —
            a searched patient may have no priority, no carrier and no work at
            all — so they step aside while searching and come back on clear. */}
        {!searching && (
          <>
            <Segmented
              data-onboarding-id="ftc-task-type-filter"
              value={taskType}
              onChange={(value) => {
                setPage(1);
                setTaskType(value);
              }}
              options={TASK_TYPE_OPTIONS}
            />
            {/* Whose desk: hidden under an assignee deep link, which already
                names one person. */}
            {!assignee ? (
              <Segmented
                data-onboarding-id="ftc-scope-filter"
                value={scope || "mine"}
                onChange={(value) => {
                  setPage(1);
                  setScope(value);
                }}
                options={[{ value: "mine", label: "Mine" }, { value: "all", label: "Everyone" }]}
              />
            ) : null}
          </>
        )}
        {assignee ? (
          // No dropdown for this: it arrives by link and the only thing anyone
          // wants to do with it is drop it. A removable tag says both.
          <Tag
            closable
            color="purple"
            onClose={() => {
              setPage(1);
              setAssignee(null);
            }}
            style={{ marginInlineEnd: 0, padding: "4px 8px" }}
          >
            Assigned to {assignee.label || "one FTC"}
          </Tag>
        ) : null}
        <Select
          data-onboarding-id="ftc-clinic-filter"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ minWidth: 200 }}
          placeholder="All clinics"
          value={clinicId}
          onChange={(value) => {
            setPage(1);
            setClinicId(value);
          }}
          options={clinicOptions}
        />
        {!searching && (
          <>
            <Select
              data-onboarding-id="ftc-priority-filter"
              allowClear
              style={{ minWidth: 160 }}
              placeholder="All priorities"
              value={priority}
              onChange={(value) => {
                setPage(1);
                setPriority(value);
              }}
              options={PRIORITY_OPTIONS}
            />
            <Select
              data-onboarding-id="ftc-carriers-filter"
              mode="multiple"
              allowClear
              style={{ minWidth: 240 }}
              placeholder="All carriers"
              value={carriers}
              onChange={(value) => {
                setPage(1);
                setCarriers(value);
              }}
              options={carrierOptions}
              maxTagCount="responsive"
              optionFilterProp="label"
            />
          </>
        )}
        <div className="ftc-tasks-filters-right">
          <Input
            allowClear
            className="ftc-search-input"
            placeholder="Search all patients"
            prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {!searching && (
            <span data-onboarding-id="ftc-view-toggle">
              <Segmented value={groupBy} onChange={(value) => setGroupBy(value)} options={VIEW_OPTIONS} />
            </span>
          )}
        </div>
      </div>

      {/* Search results split two ways: everyone who matched, and just the ones
          with outstanding FTC work. */}
      {searching && (
        <div className="ftc-search-scope">
          <Segmented
            value={searchScope}
            onChange={(value) => {
              setPage(1);
              setSearchScope(value);
            }}
            options={[
              { label: `All Patients (${searchCounts.all})`, value: "all" },
              { label: `Patients with Tasks (${searchCounts.tasks})`, value: "tasks" },
            ]}
          />
        </div>
      )}

      {grouped ? (
        loading && groups.length === 0 ? (
          <div className="ftc-grouped-loading">
            <Spin />
          </div>
        ) : groups.length === 0 ? (
          <Empty description="You're all caught up — no outstanding FTC tasks." />
        ) : (
          <div className="ftc-carrier-groups">
            {groups.map((g) => {
              const key = g.carrier_code || "other";
              const isCollapsed = collapsedGroups.has(key);
              const swatch = g.carrier_code ? carrierSwatch(g.carrier_code) : NEUTRAL_SWATCH;
              const counts = priorityCounts(g.tasks);
              return (
                <section key={key} className={"ftc-carrier-group" + (isCollapsed ? " is-collapsed" : "")}>
                  <div
                    className="ftc-carrier-group-header"
                    role="button"
                    tabIndex={0}
                    aria-expanded={!isCollapsed}
                    onClick={() => toggleGroup(key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleGroup(key);
                      }
                    }}
                  >
                    <span
                      className="ftc-carrier-badge"
                      style={{ color: swatch.color, background: swatch.bg, borderColor: swatch.border }}
                    >
                      {carrierInitials(g.carrier_name, g.carrier_code)}
                    </span>
                    <span className="ftc-carrier-group-name">{g.carrier_name}</span>
                    <Tooltip title={`${g.count} task${g.count === 1 ? "" : "s"}`}>
                      <span className="ftc-carrier-group-count">{g.count}</span>
                    </Tooltip>
                    <div className="ftc-carrier-pri-summary">
                      {PRIORITY_ORDER.map((k) =>
                        counts[k] ? (
                          <Tooltip key={k} title={`${counts[k]} ${PRI_SUMMARY_LABEL[k]}`}>
                            <span
                              className="ftc-pri-mini"
                              style={{
                                color: PRIORITY_META[k].color,
                                background: PRIORITY_META[k].bg,
                                borderColor: PRIORITY_META[k].border,
                              }}
                            >
                              <span className="ftc-pri-dot" style={{ background: PRIORITY_META[k].color }} />
                              {counts[k]}
                            </span>
                          </Tooltip>
                        ) : null,
                      )}
                    </div>
                    <DownOutlined className="ftc-carrier-chevron" />
                  </div>
                  {!isCollapsed ? (
                    <div className="ftc-carrier-group-body">
                      <Table
                        rowKey={(row) => row.task_key || row.patient.id}
                        columns={groupedColumns}
                        dataSource={g.tasks}
                        pagination={false}
                        showHeader={false}
                        size="small"
                      />
                    </div>
                  ) : null}
                </section>
              );
            })}
          </div>
        )
      ) : (
        <div data-onboarding-id="ftc-task-table">
          <Table
            rowKey={(row) => row.task_key || row.patient.id}
            columns={flatColumns}
            dataSource={tasks}
            loading={loading}
            locale={{
              emptyText: (
                <Empty
                  description={
                    searching
                      ? searchScope === "tasks"
                        ? "No matching patients have outstanding tasks."
                        : "No patients match that search."
                      : "You're all caught up — no outstanding FTC tasks."
                  }
                />
              ),
            }}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: count,
              showSizeChanger: true,
              pageSizeOptions: ["10", "25", "50", "100"],
              showTotal: (total, range) => range[0] + "-" + range[1] + " of " + total,
              onChange: (nextPage, nextSize) => {
                setPage(nextPage);
                setPageSize(nextSize);
              },
            }}
          />
        </div>
      )}

      {/*
        Headless insurance modal opened by a clicked work chip. Reuses the
        row-actions component (hideTriggers) and keys it by patient+tab+policy so
        each click remounts and re-opens on the right tab with the right policy.
      */}
      {insuranceTarget ? (
        <TreatmentPlanListRowInsuranceActions
          key={`${insuranceTarget.patient.id}:${insuranceTarget.tab}:${insuranceTarget.policyId}`}
          patient={insuranceTarget.patient}
          officeId={insuranceTarget.officeId}
          hideTriggers
          autoOpenInsuranceTab={insuranceTarget.tab}
          defaultSelectedPolicyId={insuranceTarget.policyId}
          outcomeSuggestion={insuranceTarget.suggestion}
          onPatientInsuranceUpdated={refetch}
          onInsuranceModalClosed={() => {
            setInsuranceTarget(null);
            refetch();
          }}
        />
      ) : null}

      {/* Data-entry chip target. Keyed by policy so a second click on a
          different row remounts and refetches that policy. */}
      {dataEntryTarget ? (
        <FTCDataEntryModal
          key={dataEntryTarget.policyId}
          patientId={dataEntryTarget.patientId}
          policyId={dataEntryTarget.policyId}
          onClose={() => setDataEntryTarget(null)}
          onSaved={() => {
            setDataEntryTarget(null);
            refetch();
          }}
        />
      ) : null}
    </div>
    </DashboardLayout>
  );
}
